import { ReactNode } from 'react'
import styled from 'styled-components'

import { Icon, Loader, Text } from '@percent/lemonade'

import { DEFAULT_CHART_HEIGHT } from '../constants'

interface InsightsCardBodyProps {
  /** Latest error from the metric query, if any. */
  error: unknown
  /** True while the query has yet to resolve for the first time. */
  isLoading: boolean
  /** Truthy when the query has data to render. */
  hasData: boolean
  /** Stable test-id base for the loader/error nodes. */
  testId: string
  /** Height the loader/empty states occupy, so the card doesn't jump. */
  height?: number
  /** Error message (host-localised). Defaults to a neutral English string. */
  errorLabel?: string
  /** True when the chosen chart can't render this metric's data shape (a dev error). */
  incompatible?: boolean
  /** Empty state shown when the query loaded with no rows. Defaults to an icon. */
  emptyState?: ReactNode
  /** The fully-loaded body, rendered when `hasData` is truthy. */
  children: ReactNode
}

const Centered = styled.div<{ $height: number }>`
  ${({ theme, $height }) => `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.sizes.xs};
    height: ${$height}px;
    width: 100%;
  `}
`

// Resolves the body of any insights chart: error first (a cold-start failure
// shows inline), then loader, then empty state, then the chart. While
// keepPreviousData shows the last response, `hasData` stays true so a
// later-failing refetch is intentionally suppressed and the user keeps seeing
// the last known data.
export function InsightsCardBody({
  error,
  isLoading,
  hasData,
  incompatible,
  testId,
  height = DEFAULT_CHART_HEIGHT,
  errorLabel = 'Failed to load',
  emptyState,
  children,
}: Readonly<InsightsCardBodyProps>) {
  if (error && !hasData) {
    return (
      <Text size="small" dataTestId={`${testId}-error`}>
        {errorLabel}
      </Text>
    )
  }

  if (isLoading && !hasData) {
    return (
      <Centered $height={height} data-testid={`${testId}-loader`}>
        <Loader />
      </Centered>
    )
  }

  // A chart/metric mismatch (e.g. a donut pointed at a scalar) is a developer
  // error — the dev controls both the metric and the chart. Degrade gracefully
  // with a fixed dev-facing message rather than crashing or showing a blank/empty
  // card; this isn't a localised end-user state.
  if (incompatible) {
    return (
      <Centered $height={height} data-testid={`${testId}-unsupported`}>
        <Text size="small">
          This metric can&apos;t be shown as this chart type
        </Text>
      </Centered>
    )
  }

  if (!hasData) {
    return (
      <Centered $height={height} data-testid={`${testId}-empty`}>
        {emptyState ?? <Icon name="empty" size={6} color="gray400" />}
      </Centered>
    )
  }

  return <>{children}</>
}
